import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Building2,
  Users,
  AlertTriangle,
  Crown,
  Plus,
  Pencil,
  Trash2,
  BarChart3,
  TrendingUp,
  FileText,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Search,
  ChevronRight,
  Building,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { maskCNPJ, cleanCNPJ, validateCNPJ } from '../utils/brazilValidation';
import { resetMasterOnboarding } from '../components/MasterOnboarding';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MasterDashboard() {
  const { isMaster, managedCompanies, refreshCompanies, switchCompany, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [reports, setReports] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cnpjError, setCnpjError] = useState('');
  
  const [companyForm, setCompanyForm] = useState({
    nome: '',
    cnpj: '',
    razao_social: '',
    endereco: '',
    telefone: '',
    email: ''
  });

  useEffect(() => {
    if (!isMaster) {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [isMaster, navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const fetchOptions = { credentials: 'include' };
      
      const [dashRes, reportsRes] = await Promise.all([
        fetch(`${API}/api/master/dashboard`, fetchOptions),
        fetch(`${API}/api/master/reports/consolidated`, fetchOptions)
      ]);

      if (dashRes.ok) {
        const data = await dashRes.json();
        setDashboardData(data);
      }

      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCNPJChange = (value) => {
    const masked = maskCNPJ(value);
    setCompanyForm(prev => ({ ...prev, cnpj: masked }));
    
    const cleaned = cleanCNPJ(value);
    if (cleaned.length === 14) {
      if (!validateCNPJ(cleaned)) {
        setCnpjError('CNPJ inválido');
      } else {
        setCnpjError('');
      }
    } else {
      setCnpjError('');
    }
  };

  const handleAddCompany = async () => {
    if (!companyForm.nome.trim()) {
      toast.error('Informe o nome da empresa');
      return;
    }
    
    if (!companyForm.cnpj || cleanCNPJ(companyForm.cnpj).length !== 14) {
      toast.error('Informe um CNPJ válido');
      return;
    }
    
    if (!validateCNPJ(companyForm.cnpj)) {
      toast.error('CNPJ inválido');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API}/api/master/companies`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...companyForm,
          cnpj: cleanCNPJ(companyForm.cnpj)
        })
      });

      if (response.ok) {
        toast.success('Empresa criada com sucesso!');
        setShowAddModal(false);
        resetForm();
        await refreshCompanies();
        await fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao criar empresa');
      }
    } catch (error) {
      toast.error('Erro ao criar empresa');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditCompany = async () => {
    if (!companyForm.nome.trim()) {
      toast.error('Informe o nome da empresa');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API}/api/master/companies/${selectedCompany.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: companyForm.nome,
          razao_social: companyForm.razao_social,
          endereco: companyForm.endereco,
          telefone: companyForm.telefone,
          email: companyForm.email
        })
      });

      if (response.ok) {
        toast.success('Empresa atualizada com sucesso!');
        setShowEditModal(false);
        resetForm();
        await refreshCompanies();
        await fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao atualizar empresa');
      }
    } catch (error) {
      toast.error('Erro ao atualizar empresa');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteCompany = async () => {
    // Verify confirmation text
    if (deleteConfirmText !== selectedCompany?.nome) {
      toast.error('Digite o nome da empresa corretamente para confirmar');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await fetch(`${API}/api/master/companies/${selectedCompany.id}/permanent`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Empresa excluída! ${data.total_deleted} registros removidos.`);
        setShowDeleteConfirm(false);
        setSelectedCompany(null);
        setDeleteConfirmText('');
        await refreshCompanies();
        await fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir empresa');
      }
    } catch (error) {
      toast.error('Erro ao excluir empresa');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setCompanyForm({
      nome: '',
      cnpj: '',
      razao_social: '',
      endereco: '',
      telefone: '',
      email: ''
    });
    setCnpjError('');
    setSelectedCompany(null);
  };

  const openEditModal = (company) => {
    setSelectedCompany(company);
    setCompanyForm({
      nome: company.nome || '',
      cnpj: company.cnpj || '',
      razao_social: company.razao_social || '',
      endereco: company.endereco || '',
      telefone: company.telefone || '',
      email: company.email || ''
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (company) => {
    setSelectedCompany(company);
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };

  const handleGoToCompany = async (companyId) => {
    try {
      await switchCompany(companyId);
      toast.success('Empresa alterada com sucesso!');
      // Redirect to dashboard with the new company context
      navigate('/');
    } catch (error) {
      console.error('Error switching company:', error);
      toast.error('Erro ao acessar empresa');
    }
  };

  const filteredCompanies = dashboardData?.companies?.filter(company =>
    company.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.cnpj?.includes(searchTerm)
  ) || [];

  if (!isMaster) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0d9488]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="master-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Crown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Dashboard Master
              </h1>
              <p className="text-gray-500">Visão consolidada de todas as empresas</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost"
            size="sm"
            onClick={() => {
              resetMasterOnboarding(user?.id);
              navigate('/');
              toast.success('Tour reiniciado! Volte ao Dashboard para ver.');
            }}
            className="text-gray-500 hover:text-[#0d9488]"
            title="Ver tour novamente"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            onClick={fetchDashboardData}
            className="border-[#0d9488]/30"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          {user?.role === 'admin' && (
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-[#0d9488] hover:bg-[#0f766e]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Empresa
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Total Empresas</p>
                <p className="text-3xl font-bold text-amber-900">{dashboardData?.total_companies || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-200 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Total Funcionários</p>
                <p className="text-3xl font-bold text-blue-900">{dashboardData?.total_employees || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-200 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700">Total Riscos</p>
                <p className="text-3xl font-bold text-orange-900">{dashboardData?.total_risks || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-200 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-orange-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Riscos Críticos</p>
                <p className="text-3xl font-bold text-red-900">{dashboardData?.high_risks || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-200 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#0d9488]/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#0d9488]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avaliações</p>
                <p className="text-2xl font-bold text-[#1e3a5f]">{dashboardData?.total_assessments || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Treinamentos</p>
                <p className="text-2xl font-bold text-[#1e3a5f]">{dashboardData?.total_trainings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Taxa de Conclusão</p>
                <p className="text-2xl font-bold text-[#1e3a5f]">{reports?.assessments?.completion_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Distribution */}
      {reports?.risks_by_level && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#0d9488]" />
              Distribuição de Riscos Consolidada
            </CardTitle>
            <CardDescription>Visão geral dos riscos em todas as empresas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {Object.entries(reports.risks_by_level).map(([level, count]) => {
                const colors = {
                  'Baixo': 'bg-emerald-100 text-emerald-800 border-emerald-200',
                  'Médio': 'bg-amber-100 text-amber-800 border-amber-200',
                  'Alto': 'bg-orange-100 text-orange-800 border-orange-200',
                  'Crítico': 'bg-red-100 text-red-800 border-red-200'
                };
                return (
                  <div key={level} className={`p-4 rounded-xl border ${colors[level] || 'bg-gray-100'}`}>
                    <p className="text-sm font-medium">{level}</p>
                    <p className="text-3xl font-bold">{count}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Companies List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-[#0d9488]" />
                Empresas Gerenciadas
              </CardTitle>
              <CardDescription>
                {dashboardData?.total_companies || 0} empresa(s) vinculada(s) ao plano Master
              </CardDescription>
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome ou CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredCompanies.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nenhuma empresa encontrada</p>
              </div>
            ) : (
              filteredCompanies.map((company) => (
                <div
                  key={company.id}
                  className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                    company.is_parent 
                      ? 'bg-gradient-to-r from-amber-50 to-transparent border-amber-200' 
                      : 'bg-white border-gray-200 hover:border-[#0d9488]/30'
                  }`}
                  data-testid={`company-card-${company.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        company.is_parent 
                          ? 'bg-gradient-to-br from-amber-400 to-amber-600' 
                          : 'bg-gradient-to-br from-[#1e3a5f] to-[#0d9488]'
                      }`}>
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#1e3a5f]">{company.nome}</h3>
                          {company.is_parent && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <Crown className="h-3 w-3 mr-1" />
                              Principal
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{company.cnpj}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {company.employees || company.stats?.employees || 0} funcionários
                          </span>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {company.risks || company.stats?.risks || 0} riscos
                          </span>
                          {(company.high_risks || company.stats?.high_risks > 0) && (
                            <span className="text-xs text-red-500 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {company.high_risks || company.stats?.high_risks} críticos
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.role === 'admin' && !company.is_parent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(company)}
                          className="text-gray-500 hover:text-[#0d9488]"
                          data-testid={`edit-company-${company.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {user?.role === 'superadmin' && !company.is_parent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteConfirm(company)}
                          className="text-gray-500 hover:text-red-500"
                          data-testid={`delete-company-${company.id}`}
                          title="Excluir permanentemente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGoToCompany(company.id)}
                        className="border-[#0d9488]/30 text-[#0d9488] hover:bg-[#0d9488]/10"
                        data-testid={`access-company-${company.id}`}
                      >
                        Acessar
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Company Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#0d9488]" />
              Adicionar Nova Empresa
            </DialogTitle>
            <DialogDescription>
              Cadastre uma nova empresa vinculada ao seu plano Master.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="add-nome">Nome da Empresa *</Label>
              <Input
                id="add-nome"
                value={companyForm.nome}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Nome fantasia"
              />
            </div>

            <div>
              <Label htmlFor="add-cnpj">CNPJ *</Label>
              <Input
                id="add-cnpj"
                value={companyForm.cnpj}
                onChange={(e) => handleCNPJChange(e.target.value)}
                placeholder="00.000.000/0000-00"
                className={cnpjError ? 'border-red-500' : ''}
                maxLength={18}
              />
              {cnpjError && (
                <p className="text-sm text-red-500 mt-1">{cnpjError}</p>
              )}
            </div>

            <div>
              <Label htmlFor="add-razao">Razão Social</Label>
              <Input
                id="add-razao"
                value={companyForm.razao_social}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, razao_social: e.target.value }))}
                placeholder="Razão Social"
              />
            </div>

            <div>
              <Label htmlFor="add-email">E-mail</Label>
              <Input
                id="add-email"
                type="email"
                value={companyForm.email}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contato@empresa.com.br"
              />
            </div>

            <div>
              <Label htmlFor="add-telefone">Telefone</Label>
              <Input
                id="add-telefone"
                value={companyForm.telefone}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, telefone: e.target.value }))}
                placeholder="(11) 99999-9999"
              />
            </div>

            <div>
              <Label htmlFor="add-endereco">Endereço</Label>
              <Input
                id="add-endereco"
                value={companyForm.endereco}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, endereco: e.target.value }))}
                placeholder="Rua, número, cidade - UF"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddCompany}
              disabled={processing}
              className="bg-[#0d9488] hover:bg-[#0f766e]"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Empresa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-[#0d9488]" />
              Editar Empresa
            </DialogTitle>
            <DialogDescription>
              Atualize os dados da empresa {selectedCompany?.nome}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-nome">Nome da Empresa *</Label>
              <Input
                id="edit-nome"
                value={companyForm.nome}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Nome fantasia"
              />
            </div>

            <div>
              <Label htmlFor="edit-cnpj">CNPJ</Label>
              <Input
                id="edit-cnpj"
                value={companyForm.cnpj}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">O CNPJ não pode ser alterado</p>
            </div>

            <div>
              <Label htmlFor="edit-razao">Razão Social</Label>
              <Input
                id="edit-razao"
                value={companyForm.razao_social}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, razao_social: e.target.value }))}
                placeholder="Razão Social"
              />
            </div>

            <div>
              <Label htmlFor="edit-email">E-mail</Label>
              <Input
                id="edit-email"
                type="email"
                value={companyForm.email}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contato@empresa.com.br"
              />
            </div>

            <div>
              <Label htmlFor="edit-telefone">Telefone</Label>
              <Input
                id="edit-telefone"
                value={companyForm.telefone}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, telefone: e.target.value }))}
                placeholder="(11) 99999-9999"
              />
            </div>

            <div>
              <Label htmlFor="edit-endereco">Endereço</Label>
              <Input
                id="edit-endereco"
                value={companyForm.endereco}
                onChange={(e) => setCompanyForm(prev => ({ ...prev, endereco: e.target.value }))}
                placeholder="Rua, número, cidade - UF"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleEditCompany}
              disabled={processing}
              className="bg-[#0d9488] hover:bg-[#0f766e]"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog - Critical Action */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => {
        if (!open) {
          setDeleteConfirmText('');
        }
        setShowDeleteConfirm(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              ATENÇÃO: Exclusão Permanente
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium mb-2">
                Você está prestes a excluir permanentemente a empresa:
              </p>
              <p className="text-red-900 font-bold text-lg">
                {selectedCompany?.nome}
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-amber-800 text-sm font-medium mb-2">
                Esta ação irá EXCLUIR PERMANENTEMENTE:
              </p>
              <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
                <li>Todos os usuários da empresa</li>
                <li>Todos os funcionários e seus dados</li>
                <li>Todas as avaliações e respostas</li>
                <li>Todos os riscos e planos de ação</li>
                <li>Todos os treinamentos e certificados</li>
                <li>Todos os arquivos no storage</li>
                <li>Todas as configurações e integrações</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-gray-700 text-sm mb-3">
                <strong>Esta ação é IRREVERSÍVEL.</strong> Para confirmar, digite o nome da empresa abaixo:
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={selectedCompany?.nome}
                className="border-red-200 focus:border-red-500 focus:ring-red-500"
                data-testid="delete-confirm-input"
              />
              {deleteConfirmText && deleteConfirmText !== selectedCompany?.nome && (
                <p className="text-red-500 text-xs mt-1">
                  O nome digitado não corresponde
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmText('');
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteCompany}
              disabled={processing || deleteConfirmText !== selectedCompany?.nome}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir Permanentemente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
